/**
 * p1-web — Farmer detail + propose-edit flow.
 *
 * Reached from the Farmers list (a row links to `/farmers/$userId`); it is
 * intentionally NOT in the sidebar nav (no `_app.tsx` change) — a detail
 * page has no top-level home.
 *
 * Data source (TD-071): this page reads the dedicated `GET /farmers/:id`
 * endpoint (services/api/src/farmers/farmers.controller.ts) via the manual
 * `farmersControllerGetOne` client, keyed `['farmer', userId]`. It replaces
 * the old "fetch list@100 then .find()" workaround that predated the
 * single-farmer route. A 404 renders the "not found" card; a 403 (coop_admin
 * reaching a farmer outside their coop) renders the same friendly message.
 * After a propose-edit we invalidate BOTH `['farmer', userId]` (this view)
 * and `['farmers']` (the roster) so the pending-count badge bumps on both.
 *
 * A "Contributions" section below the profile lists this farmer's recorded
 * harvest contributions via `GET /contributions?farmerUserId=<id>`, keyed
 * `['farmer-contributions', userId]`.
 *
 * "Edit farmer" is NOT a direct mutation. It POSTs a
 * `farmer_profile_edit_requests` row that a coop_admin later approves in
 * Pending Reviews. We send only the fields that actually changed
 * (proposed_* are all nullable/optional per CreateEditRequestDto), plus the
 * required target `userId`. On success we invalidate so the pending-count
 * badge bumps.
 *
 * Role gate: the propose-edit affordance is admin / coop_admin only. The
 * whole farmers surface is `@Roles('admin','coop_admin')` server-side and
 * the _app layout bounces sysCollab, so a `farmer` never reaches here; the
 * self-edit path lives in the mobile app. We still gate in the UI so the
 * affordance never renders for an unexpected role.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Text,
  TextField,
  Typography,
} from '@fse/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { readAccessTokenClaims, FseApiError } from '../api/client';
import { farmersControllerGetOne } from '../api/generated/farmers/farmers';
import { contributionsControllerList } from '../api/generated/contributions/contributions';
import { useEditRequestsControllerCreate } from '../api/generated/farmer-profile-edit-requests/farmer-profile-edit-requests';
import type { ContributionDto, CreateEditRequestDto, FarmerDto } from '../api/generated/models';

type PreferredLang = FarmerDto['preferredLang'];

// Language self-labels (endonyms) — intentionally NOT translated; each label is
// the language's own name in its own script. Mirrors LANGUAGE_LABELS in
// signup.tsx + PREF_LANGS in _app.farmers.new.tsx — keep in sync.
const LANG_LABEL: Record<PreferredLang, string> = {
  en: 'English',
  ts: 'Xitsonga',
  nso: 'Sepedi',
  xi: 'isiXhosa',
};

// Principal-type labels are user-facing copy, so they resolve through t() at
// render and follow the active language (TD-072).
function principalLabel(t: TFunction, principalType: FarmerDto['principalType']): string {
  const key: Record<FarmerDto['principalType'], string> = {
    individual: 'farmerDetail.principal.individual',
    informal_group: 'farmerDetail.principal.informalGroup',
    formal_coop: 'farmerDetail.principal.formalCoop',
  };
  return key[principalType] ? t(key[principalType]) : '';
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function fmtGps(gps: FarmerDto['farmGps']): string {
  if (!gps) return '—';
  return `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}`;
}

function canPropose(): boolean {
  const role = readAccessTokenClaims()?.role;
  return role === 'admin' || role === 'coop_admin';
}

export function FarmerDetailScreen() {
  const { t } = useTranslation();
  // `strict: false` returns a loosely-typed params bag — the route tree is
  // assembled in router.ts (no router plugin), so the typed route-id literal
  // for `from` isn't reachable from here without a circular import.
  const { userId } = useParams({ strict: false }) as { userId: string };

  const farmerQuery = useQuery({
    queryKey: ['farmer', userId],
    queryFn: ({ signal }) => farmersControllerGetOne(userId, signal),
    // 404 (no such farmer) and 403 (farmer outside the caller's coop) are
    // expected "not found" states for this page, not transient failures —
    // don't retry them. Other errors fall back to the default retry.
    retry: (failureCount, error) => {
      if (
        error instanceof FseApiError &&
        (error.status === 404 || error.status === 403)
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Treat 404/403 as "not found" rather than a hard error card — same friendly
  // message either way (the caller shouldn't learn whether the farmer exists
  // in another coop).
  const isNotFound =
    farmerQuery.isError &&
    farmerQuery.error instanceof FseApiError &&
    (farmerQuery.error.status === 404 || farmerQuery.error.status === 403);

  return (
    <Stack spacing={3}>
      <Box>
        <Link to="/farmers" style={{ textDecoration: 'none' }}>
          <Button variant="text" size="small" startIcon={<Icon name="arrow_back" size={20} />}>
            {t('farmerDetail.back')}
          </Button>
        </Link>
      </Box>

      {farmerQuery.isPending ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Skeleton variant="text" width={240} height={40} />
              <Skeleton variant="rectangular" height={200} />
            </Stack>
          </CardContent>
        </Card>
      ) : isNotFound ? (
        <Card>
          <CardHeader title={t('farmerDetail.notFound.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('farmerDetail.notFound.body')}
            </Text>
          </CardContent>
        </Card>
      ) : farmerQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => farmerQuery.refetch()}>
              {t('farmerDetail.retry')}
            </Button>
          }
        >
          {t('farmerDetail.loadError')}{' '}
          {farmerQuery.error instanceof FseApiError
            ? farmerQuery.error.message
            : String(farmerQuery.error)}
        </Alert>
      ) : (
        <FarmerDetail farmer={farmerQuery.data} />
      )}
    </Stack>
  );
}

function FarmerDetail({ farmer }: { farmer: FarmerDto }) {
  const { t } = useTranslation();
  const [proposeOpen, setProposeOpen] = useState(false);
  const showPropose = canPropose();
  const principal = principalLabel(t, farmer.principalType);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Text variant="headline-small" as="span">
                {farmer.displayName}
              </Text>
              {/* Guard: an API build predating TD-057 omits principalType, and any
                  unmapped value yields no label — render nothing rather than a blank chip. */}
              {principal ? (
                <Chip kind="assist" color="neutral" label={principal} />
              ) : null}
              {farmer.pendingEditRequestCount > 0 ? (
                <Chip
                  kind="assist"
                  color="warning"
                  leadingIcon="schedule"
                  label={t('farmerDetail.pendingEdits', { count: farmer.pendingEditRequestCount })}
                />
              ) : null}
            </Stack>
          }
          subheader={
            <Typography variant="caption" color="text.secondary">
              {t('farmerDetail.registered', {
                date: dateFormatter.format(new Date(farmer.registeredAt)),
              })}
            </Typography>
          }
          action={
            showPropose && !proposeOpen ? (
              <Button
                variant="contained"
                onClick={() => setProposeOpen(true)}
                startIcon={<Icon name="edit" size={20} />}
              >
                {t('farmerDetail.proposeEdit')}
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          <Stack spacing={0.5} divider={<Divider flexItem />}>
            <DetailRow label={t('farmerDetail.field.phone')} value={farmer.phone} mono />
            <DetailRow label={t('farmerDetail.field.preferredLang')} value={LANG_LABEL[farmer.preferredLang]} />
            <DetailRow
              label={t('farmerDetail.field.cooperative')}
              value={farmer.cooperativeName ?? t('farmerDetail.independent')}
              muted={farmer.cooperativeName === null}
            />
            <DetailRow label={t('farmerDetail.field.treeCount')} value={String(farmer.treeCount)} />
            <DetailRow
              label={t('farmerDetail.field.estCapacity')}
              value={farmer.estCapacityKg == null ? '—' : String(farmer.estCapacityKg)}
              muted={farmer.estCapacityKg == null}
            />
            <DetailRow
              label={t('farmerDetail.field.farmGps')}
              value={fmtGps(farmer.farmGps)}
              mono={farmer.farmGps !== null}
              muted={farmer.farmGps === null}
            />
          </Stack>
        </CardContent>
      </Card>

      {showPropose && proposeOpen ? (
        <ProposeEditForm farmer={farmer} onClose={() => setProposeOpen(false)} />
      ) : null}

      <ContributionsSection userId={farmer.userId} />
    </>
  );
}

// --------------------------------------------------------------------------
// Contributions section
//
// Lists this farmer's recorded harvest contributions via
// `GET /contributions?farmerUserId=<id>` (the server scopes a coop_admin to
// their own cooperative). qtyKg arrives as a numeric(10,2) string, so we
// parseFloat before formatting. Loading / empty / error states mirror the
// rest of the page.
// --------------------------------------------------------------------------

const qtyFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtHarvestDate(iso: string): string {
  // harvestDate is YYYY-MM-DD (no time) — parse as a local date to avoid the
  // UTC-midnight off-by-one a bare `new Date('YYYY-MM-DD')` can introduce.
  const parts = iso.split('-').map(Number);
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    return dateFormatter.format(new Date(parts[0], parts[1] - 1, parts[2]));
  }
  return iso;
}

function fmtQtyKg(qtyKg: ContributionDto['qtyKg']): string {
  const n = parseFloat(qtyKg);
  return Number.isFinite(n) ? `${qtyFormatter.format(n)} kg` : qtyKg;
}

function ContributionsSection({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const contributionsQuery = useQuery({
    queryKey: ['farmer-contributions', userId],
    queryFn: ({ signal }) => contributionsControllerList({ farmerUserId: userId }, signal),
  });

  return (
    <Card>
      <CardHeader
        title={
          <Text variant="title-large" as="span">
            {t('farmerDetail.contributions.title')}
          </Text>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {t('farmerDetail.contributions.subtitle')}
          </Typography>
        }
      />
      <CardContent>
        {contributionsQuery.isPending ? (
          <Stack spacing={1}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
          </Stack>
        ) : contributionsQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => contributionsQuery.refetch()}
              >
                {t('farmerDetail.retry')}
              </Button>
            }
          >
            {t('farmerDetail.contributions.loadError')}{' '}
            {contributionsQuery.error instanceof FseApiError
              ? contributionsQuery.error.message
              : String(contributionsQuery.error)}
          </Alert>
        ) : contributionsQuery.data.data.length === 0 ? (
          <Text variant="body-medium" color="text.secondary">
            {t('farmerDetail.contributions.empty')}
          </Text>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">{t('farmerDetail.contributions.quantity')}</TableCell>
                <TableCell>{t('farmerDetail.contributions.grade')}</TableCell>
                <TableCell>{t('farmerDetail.contributions.harvestDate')}</TableCell>
                <TableCell>{t('farmerDetail.contributions.recorded')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contributionsQuery.data.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                    {fmtQtyKg(c.qtyKg)}
                  </TableCell>
                  <TableCell>
                    {c.qualityGrade ? (
                      <Chip kind="assist" color="success" label={t('farmerDetail.contributions.gradeValue', { grade: c.qualityGrade })} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{fmtHarvestDate(c.harvestDate)}</TableCell>
                  <TableCell>{dateFormatter.format(new Date(c.recordedAt))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
  muted = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '200px 1fr',
        alignItems: 'baseline',
        gap: 1,
        py: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body1"
        color={muted ? 'text.secondary' : 'text.primary'}
        sx={mono ? { fontFamily: 'monospace' } : undefined}
      >
        {value}
      </Typography>
    </Box>
  );
}

// --------------------------------------------------------------------------
// Propose-edit dialog
//
// Every field starts pre-filled from the current value. On submit we diff
// the form against the farmer and send ONLY the changed fields as proposed_*
// — CreateEditRequestDto makes all proposed_* nullable/optional and the
// server refuses an empty request (at least one proposed_* or a note). We
// always send the required target `userId`.
// --------------------------------------------------------------------------

interface FormState {
  displayName: string;
  preferredLang: PreferredLang;
  treeCount: string;
  estCapacityKg: string;
  lat: string;
  lng: string;
  note: string;
}

function initialForm(farmer: FarmerDto): FormState {
  return {
    displayName: farmer.displayName,
    preferredLang: farmer.preferredLang,
    treeCount: String(farmer.treeCount),
    estCapacityKg: farmer.estCapacityKg == null ? '' : String(farmer.estCapacityKg),
    lat: farmer.farmGps ? String(farmer.farmGps.lat) : '',
    lng: farmer.farmGps ? String(farmer.farmGps.lng) : '',
    note: '',
  };
}

function ProposeEditForm({
  farmer,
  onClose,
}: {
  farmer: FarmerDto;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => initialForm(farmer));
  const [validationError, setValidationError] = useState<string | null>(null);

  const createMutation = useEditRequestsControllerCreate({
    mutation: {
      onSuccess: () => {
        // Bump the pending-count badge on both the roster (['farmers', ...])
        // and this detail view (['farmer', userId] — now its own key family
        // after the TD-071 repoint). Then close + go to the review queue so
        // the proposer can see it landed.
        queryClient.invalidateQueries({ queryKey: ['farmers'] });
        queryClient.invalidateQueries({ queryKey: ['farmer', farmer.userId] });
        onClose();
        navigate({ to: '/pending-reviews' });
      },
    },
  });

  // Build the diff payload. Returns null + sets a validation message when
  // nothing changed (mirrors the server's "at least one proposed_* or a
  // note" refinement so the user gets feedback before the round-trip).
  const buildPayload = (): CreateEditRequestDto | null => {
    const payload: CreateEditRequestDto = { userId: farmer.userId };

    const displayName = form.displayName.trim();
    if (displayName !== farmer.displayName && displayName.length > 0) {
      payload.proposedDisplayName = displayName;
    }

    if (form.preferredLang !== farmer.preferredLang) {
      payload.proposedPreferredLang = form.preferredLang;
    }

    const treeCount = Number(form.treeCount);
    if (
      /^\d+$/.test(form.treeCount.trim()) &&
      Number.isInteger(treeCount) &&
      treeCount !== farmer.treeCount
    ) {
      payload.proposedTreeCount = treeCount;
    }

    const capTrim = form.estCapacityKg.trim();
    if (capTrim !== '' && /^\d+$/.test(capTrim)) {
      const cap = Number(capTrim);
      if (cap !== farmer.estCapacityKg) {
        payload.proposedEstCapacityKg = cap;
      }
    }

    const latTrim = form.lat.trim();
    const lngTrim = form.lng.trim();
    if (latTrim !== '' && lngTrim !== '') {
      const lat = Number(latTrim);
      const lng = Number(lngTrim);
      const gpsChanged =
        !farmer.farmGps || farmer.farmGps.lat !== lat || farmer.farmGps.lng !== lng;
      if (Number.isFinite(lat) && Number.isFinite(lng) && gpsChanged) {
        payload.proposedFarmGps = { lat, lng };
      }
    }

    const note = form.note.trim();
    if (note.length > 0) {
      payload.note = note;
    }

    const hasChange =
      payload.proposedDisplayName != null ||
      payload.proposedPreferredLang != null ||
      payload.proposedTreeCount != null ||
      payload.proposedEstCapacityKg != null ||
      payload.proposedFarmGps != null ||
      (payload.note != null && payload.note.length > 0);

    return hasChange ? payload : null;
  };

  const handleSubmit = () => {
    setValidationError(null);
    const payload = buildPayload();
    if (!payload) {
      setValidationError(t('farmerDetail.propose.noChanges'));
      return;
    }
    createMutation.mutate({ data: payload });
  };

  const handleClose = () => {
    if (createMutation.isPending) return;
    setForm(initialForm(farmer));
    setValidationError(null);
    createMutation.reset();
    onClose();
  };

  // Cheap enough to recompute each render — drives the "N changes" hint.
  const diffPreview = buildPayload();
  const changedCount = diffPreview
    ? (
        [
          'proposedDisplayName',
          'proposedPreferredLang',
          'proposedTreeCount',
          'proposedEstCapacityKg',
          'proposedFarmGps',
        ] as const
      ).filter((k) => diffPreview[k] != null).length
    : 0;

  return (
    <Card variant="outlined">
      <CardHeader
        title={t('farmerDetail.propose.title')}
        subheader={
          <Typography variant="body2" color="text.secondary">
            {t('farmerDetail.propose.subtitle', { name: farmer.displayName })}
          </Typography>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <TextField
            label={t('farmerDetail.propose.displayName')}
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            fullWidth
            disabled={createMutation.isPending}
          />

          <FormControl fullWidth disabled={createMutation.isPending}>
            <InputLabel id="propose-lang-label">{t('farmerDetail.propose.preferredLang')}</InputLabel>
            <Select
              labelId="propose-lang-label"
              label={t('farmerDetail.propose.preferredLang')}
              value={form.preferredLang}
              onChange={(e) =>
                setForm({ ...form, preferredLang: e.target.value as PreferredLang })
              }
            >
              {(Object.keys(LANG_LABEL) as PreferredLang[]).map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {LANG_LABEL[lang]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label={t('farmerDetail.propose.treeCount')}
            value={form.treeCount}
            onChange={(e) => setForm({ ...form, treeCount: e.target.value })}
            helperText={t('farmerDetail.propose.treeCountHint')}
            fullWidth
            disabled={createMutation.isPending}
          />

          <TextField
            label={t('farmerDetail.propose.estCapacity')}
            value={form.estCapacityKg}
            onChange={(e) => setForm({ ...form, estCapacityKg: e.target.value })}
            helperText={t('farmerDetail.propose.estCapacityHint')}
            fullWidth
            disabled={createMutation.isPending}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={t('farmerDetail.propose.latitude')}
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              helperText={t('farmerDetail.propose.latitudeHint')}
              fullWidth
              disabled={createMutation.isPending}
            />
            <TextField
              label={t('farmerDetail.propose.longitude')}
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              helperText={t('farmerDetail.propose.longitudeHint')}
              fullWidth
              disabled={createMutation.isPending}
            />
          </Stack>

          <TextField
            label={t('farmerDetail.propose.note')}
            placeholder={t('farmerDetail.propose.notePlaceholder')}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            disabled={createMutation.isPending}
          />

          <Typography variant="caption" color="text.secondary">
            {changedCount === 0
              ? t('farmerDetail.propose.noChangesYet')
              : t('farmerDetail.propose.changesReady', { count: changedCount })}
          </Typography>

          {validationError ? <Alert severity="warning">{validationError}</Alert> : null}

          {createMutation.isError ? (
            <Alert severity="error">
              {t('farmerDetail.propose.submitError')}{' '}
              {createMutation.error instanceof FseApiError
                ? createMutation.error.message
                : String(createMutation.error)}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={handleClose} disabled={createMutation.isPending}>
              {t('farmerDetail.propose.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              startIcon={
                createMutation.isPending ? <CircularProgress size={16} /> : <Icon name="send" size={20} />
              }
            >
              {createMutation.isPending ? t('farmerDetail.propose.submitting') : t('farmerDetail.propose.submit')}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
